// services/api.js
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

// Global axios interceptor: if auth fails, clear session and send user to login
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    console.log('🚨 Axios error details:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      method: error.config?.method,
      message: error.response?.data?.message || error.message,
      data: error.response?.data,
      headers: error.response?.headers
    });
    
    // Only logout on 401 Unauthorized, not on other errors like 404, 500, etc.
    if (error.response && error.response.status === 401) {
      // Only logout if user actually has a token (i.e., was logged in)
      const hasToken = sessionStorage.getItem('token') || localStorage.getItem('token');
      if (hasToken) {
        console.warn('🔒 Axios interceptor: 401 received for', error.config?.url, '— clearing session');
        try {
          sessionStorage.clear();
          localStorage.removeItem('token');
          localStorage.removeItem('role');
        } finally {
          // Add a small delay to allow error to be handled first
          setTimeout(() => {
            window.location.href = '/login';
          }, 100);
        }
      }
    }
    return Promise.reject(error);
  }
);

// Helper to get token (prefer sessionStorage so session ends when tab is closed)
const getToken = () => sessionStorage.getItem('token') || localStorage.getItem('token');

// Utility to normalize responses from the backend which often nests payload in `res.data.data`
const _extractPayload = (payload) => {
  if (payload === null || payload === undefined) return payload;
  // Sequelize model instance
  if (payload && typeof payload === 'object' && payload.dataValues) return payload.dataValues;
  // Array of Sequelize instances
  if (Array.isArray(payload)) return payload.map((p) => (p && p.dataValues ? p.dataValues : p));
  // Already a plain object
  return payload;
};

const normalize = (res) => {
  // res is axios response
  if (!res || !res.data) return { success: false, data: null };

  // If backend uses wrapper: { success, data, meta }
  if (res.data && typeof res.data === 'object' && ('success' in res.data)) {
    const payload = res.data.data !== undefined ? res.data.data : res.data;
    return {
      success: Boolean(res.data.success),
      data: _extractPayload(payload),
      meta: res.data.meta || null,
      raw: res.data,
    };
  }

  // Otherwise, unwrap sequelize instances if present
  return { success: true, data: _extractPayload(res.data), raw: res.data };
};

export const createAdmin = async (payload) => {
  try {
    const res = await axios.post(`${API_URL}/superadmin/admins`, payload, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return { success: true, data: res.data };
  } catch (err) {
    return { success: false, error: err.response?.data?.message || 'Failed to create admin' };
  }
};

export const getAdmins = async (search = "") => {
  try {
    const params = search.trim() ? { search } : {};

    const res = await axios.get(`${API_URL}/superadmin/admins`, {
      params,
      headers: { Authorization: `Bearer ${getToken()}` },
    });

    return { success: true, data: res.data };
  } catch (err) {
    return { 
      success: false, 
      error: err.response?.data?.message || 'Failed to fetch admins' 
    };
  }
};



export const getAdminById = async (id) => {
  try {
    const res = await axios.get(`${API_URL}/superadmin/getByidAdmin/${id}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return { success: true, data: res.data };
  } catch (err) {
    return { success: false, error: err.response?.data?.message || 'Failed to fetch admin' };
  }
};

export const updateAdmin = async (id, payload) => {
  console.log('Updating admin with ID:', id, 'Payload:', payload);
  try {
    const res = await axios.put(`${API_URL}/superadmin/admins/${id}`, payload, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    console.log('Update response:---------->>>>>>>>>>>', res.data);
    return { success: true, data: res.data };
  } catch (err) {
    return { success: false, error: err.response?.data?.message || 'Update failed' };
  }
};

export const deleteAdmin = async (id) => {
  console.log('Deleting admin with ID:', id);
  try {
    const res = await axios.delete(`${API_URL}/superadmin/deleteAdmin/${id}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    console.log('Delete response:', res.data);
    return { success: true, data: res.data };
  } catch (err) {
    console.error('Delete failed:', err.response?.data || err.message);
    return { success: false, error: err.response?.data?.message || 'Delete failed' };
  }
};


export const toggleAdminStatus = async (id, currentStatus) => {
  try {
    const isActive = !currentStatus;

    const res = await axios.patch(
      `${API_URL}/superadmin/admins/${id}/deactivate`,
      { isActive },
      { headers: { Authorization: `Bearer ${getToken()}` } }
    );

    return { success: true, data: res.data };
  } catch (err) {
    return { 
      success: false, 
      error: err.response?.data?.message || "Status update failed"
    };
  }
};

/****************************Organizations Management APIs    **************************** */

export const addOrganization = async (payload, role = 'superadmin') => {
  try {
    const endpoint =
      role === 'admin'
        ? `${API_URL}/admin/organizations`
        : role === 'subadmin'
          ? `${API_URL}/sub-admin/organizations`
          : `${API_URL}/superadmin/organizations`;

    const res = await axios.post(endpoint, payload, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    const norm = normalize(res);
    return { success: norm.success, data: norm.data, meta: norm.meta };
  } catch (err) {
    // Extract error details from various response formats
    const errorData = err.response?.data || {};
    
    // Handle field-specific errors (409 Conflict - duplicate)
    if (err.response?.status === 409) {
      return {
        success: false,
        error: {
          field: errorData.field || null,
          message: errorData.message || 'Duplicate entry',
        },
      };
    }

    // Handle validation errors with details array
    if (errorData.error?.details && Array.isArray(errorData.error.details)) {
      return {
        success: false,
        error: {
          details: errorData.error.details,
          message: errorData.message || 'Validation error',
        },
      };
    }

    // Handle validation errors with single detail
    if (errorData.details && Array.isArray(errorData.details)) {
      return {
        success: false,
        error: {
          details: errorData.details,
          message: errorData.message || 'Validation error',
        },
      };
    }

    // Generic error message
    return {
      success: false,
      error: {
        message: errorData.message || err.message || 'Failed to add organization',
      },
    };
  }
};

export const getOrganizations = async (search = "", role = 'superadmin', page = 1, limit = 100, status = "all") => {
  try {
    const statusMap = {
      pending: "Pending",
      agreementuploaded: "AgreementUploaded",
      active: "Active",
      inactive: "Inactive",
      suspended: "Suspended",
      rejected: "Rejected",
    };
    const normalizedStatus =
      status && status !== "all"
        ? (statusMap[String(status).toLowerCase()] || status)
        : undefined;
    const params = search.trim()
      ? { search, page, limit, ...(normalizedStatus ? { status: normalizedStatus } : {}) }
      : { page, limit, ...(normalizedStatus ? { status: normalizedStatus } : {}) };
    const endpoint =
      role === 'admin'
        ? `${API_URL}/admin/organizations`
        : role === 'subadmin'
          ? `${API_URL}/sub-admin/organizations`
          : `${API_URL}/superadmin/organizations`;

    const res = await axios.get(endpoint, {
      params,
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return normalize(res);
  } catch (err) {
    return { 
      success: false, 
      error: err.response?.data?.message || 'Failed to fetch organizations' 
    };
  }
};

export const getOrganizationById = async (id, role = 'superadmin') => {
  try {
    const endpoint =
      role === 'admin'
        ? `${API_URL}/admin/organizations/${id}`
        : role === 'subadmin'
          ? `${API_URL}/sub-admin/organizations/${id}`
          : `${API_URL}/superadmin/organizations/${id}`;

    const res = await axios.get(endpoint, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    const norm = normalize(res);
    return { success: norm.success, data: norm.data, meta: norm.meta };
  } catch (err) {
    return { 
      success: false, 
      error: err.response?.data?.error || err.response?.data?.message || 'Failed to add organization',
      message: err.response?.data?.message || 'Failed to add organization'
    };
  }
};

export const updateOrganization = async (id, payload, role = 'superadmin') => {
  console.log('Updating organization with ID:', id, 'Payload:', payload);
  try {
    const endpoint =
      role === 'admin'
        ? `${API_URL}/admin/organizations/${id}`
        : role === 'subadmin'
          ? `${API_URL}/sub-admin/organizations/${id}`
          : `${API_URL}/superadmin/organizations/${id}`;

    const res = await axios.put(endpoint, payload, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    const norm = normalize(res);
    return { success: norm.success, data: norm.data };
  } catch (err) {
    return { 
      success: false, 
      error: err.response?.data?.error || err.response?.data?.message || 'Update failed',
      message: err.response?.data?.message || 'Update failed'
    };
  }
};

export const deleteOrganization = async (id, role = 'superadmin') => {
  console.log('Deleting organization with ID:', id);
  try {
    const endpoint =
      role === 'admin'
        ? `${API_URL}/admin/organizations/${id}`
        : role === 'subadmin'
          ? `${API_URL}/sub-admin/organizations/${id}`
          : `${API_URL}/superadmin/organizations/${id}`;

    const res = await axios.delete(endpoint, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    console.log('Delete response:', res.data);
    const norm = normalize(res);
    return { success: true, data: norm.data };
  } catch (err) {
    console.error('Delete failed:', err.response?.data || err.message);
    return { success: false, error: err.response?.data?.message || 'Delete failed' };
  }
};

// CREDIT REPORTS (Admin / SuperAdmin views)
export const getCreditReports = async ({ page = 1, limit = 10, search = '', orgId = null, role = 'superadmin' } = {}) => {
  try {
    const endpoint = role === 'admin' ? `${API_URL}/admin/credit-reports` : `${API_URL}/superadmin/credit-reports`;
    const params = { page, limit };
    if (search) params.search = search;
    if (orgId) params.orgId = orgId;

    const res = await axios.get(endpoint, {
      params,
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return { success: true, data: res.data.data, pagination: res.data.pagination };
  } catch (err) {
    return {
      success: false,
      error: err.response?.data?.error || err.response?.data?.message || 'Failed to fetch credit reports',
    };
  }
};

export const downloadCreditReport = async (reportId, role = 'superadmin') => {
  try {
    const endpoint =
      role === 'admin'
        ? `${API_URL}/admin/credit-checks/${reportId}/download`
        : `${API_URL}/superadmin/credit-checks/${reportId}/download`;

    const res = await axios.get(endpoint, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });

    const raw = res.data?.data || {};
    const shaped = {
      ...raw,
      // Normalise field name so UI can always use `downloadUrl`
      downloadUrl: raw.downloadUrl || raw.url || raw.pdfUrl || null,
    };

    return { success: true, data: shaped };
  } catch (err) {
    return {
      success: false,
      error:
        err.response?.data?.error ||
        err.response?.data?.message ||
        'Failed to download credit report',
    };
  }
};

export const resendOrganizationAgreement = async (id, role = 'superadmin') => {
  try {
    const endpoint =
      role === 'admin'
        ? '/admin/organizations'
        : role === 'subadmin'
          ? '/sub-admin/organizations'
          : '/superadmin/organizations';

    const res = await axios.post(`${API_URL}${endpoint}/${id}/resend-agreement`, {}, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    const norm = normalize(res);
    return { success: norm.success, data: norm.data };
  } catch (err) {
    return { success: false, error: err.response?.data?.message || 'Failed to resend agreement' };
  }
};

export const uploadAgreement = async (id, file, role = 'superadmin') => {
  console.log('Uploading agreement for organization ID:', id, 'File:', file, 'Role:', role);
  const formData = new FormData();
  formData.append('file', file);
  formData.append('organizationId', id);

  const endpoint =
    role === 'admin'
      ? '/admin/organizations'
      : role === 'subadmin'
        ? '/sub-admin/organizations'
        : '/superadmin/organizations';

  const response = await axios.post(
    `${API_URL}${endpoint}/upload-signed-agreement`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${getToken()}`,
      },
    }
  );

  const norm = normalize(response);
  return { success: norm.success, data: norm.data };
};

export const getLatestAgreementUrl = async (organizationId, role = 'superadmin') => {
  try {
    const endpoint =
      role === 'admin'
        ? '/admin/organizations'
        : role === 'subadmin'
          ? '/sub-admin/organizations'
          : '/superadmin/organizations';

    const res = await axios.get(`${API_URL}${endpoint}/${organizationId}/documents/latest-url`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return { success: true, data: res.data };
  } catch (err) {
    console.error('Error fetching agreement URL:', err);
    return { success: false, error: err.response?.data?.message || 'Failed to fetch agreement' };
  }
};

export const getSentAgreementUrl = async (organizationId, role = 'superadmin') => {
  try {
    const endpoint =
      role === 'admin'
        ? '/admin/organizations'
        : role === 'subadmin'
          ? '/sub-admin/organizations'
          : '/superadmin/organizations';

    const res = await axios.get(`${API_URL}${endpoint}/${organizationId}/documents/sent-url`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return { success: true, data: res.data };
  } catch (err) {
    console.error('Error fetching sent agreement URL:', err);
    return { success: false, error: err.response?.data?.message || 'Failed to fetch sent agreement' };
  }
};

export const toggleOrganizationStatus = async (id, currentStatus, role = 'superadmin') => {
  console.log('Toggling organization status for ID:', id, 'Current Status:', currentStatus);

  try {
    // Determine action expected by backend (activate/deactivate)
    const normalized = String(currentStatus).toLowerCase();
    const action = normalized === 'active' || normalized === 'true' ? 'deactivate' : 'activate';

    const endpoint =
      role === 'admin'
        ? `${API_URL}/admin/organizations/${id}/status`
        : role === 'subadmin'
          ? `${API_URL}/sub-admin/organizations/${id}/status`
          : `${API_URL}/superadmin/organizations/${id}/status`;

    const res = await axios.patch(
      endpoint,
      { action },
      { headers: { Authorization: `Bearer ${getToken()}` } }
    );

    const norm = normalize(res);
    return { success: norm.success, data: norm.data };
  } catch (err) {
    return {
      success: false,
      error: err.response?.data?.message || "Status update failed",
    };
  }
};

/****************************Organizations User APIs    **************************** */

export const createOrgUser = async (payload, role = "superadmin") => {
  try {
    const endpoint = role === "admin" 
      ? `${API_URL}/admin/org-users`
      : role === "subadmin"
        ? `${API_URL}/sub-admin/org-users`
        : `${API_URL}/superadmin/org-users`;
    const res = await axios.post(endpoint, payload, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return { success: true, data: res.data };
  } catch (err) {
    return { success: false, error: err.response?.data?.message || 'Failed to add organization user' };
  }
};


export const fetchOrgUsers = async (role = "superadmin") => {
  try {
    let endpoint;
    if (role === "admin") {
      endpoint = `${API_URL}/admin/org-users`;
    } else if (role === "subadmin") {
      endpoint = `${API_URL}/sub-admin/org-users`;
    } else {
      endpoint = `${API_URL}/superadmin/getAllOrgUsers`;
    }
    
    const res = await axios.get(endpoint, { 
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    
    // Normalize response - admin endpoint returns { success, data }, superadmin might be different
    if (res.data.success) {
      return { success: true, data: res.data.data };
    } else {
      return { success: true, data: res.data };
    }
  } catch (err) {
    return { success: false, error: err.response?.data?.message || 'Failed to fetch organization users' };
  }
};

export const deleteOrgUser = async (userId, role = "superadmin") => {
  console.log('Deleting organization user with ID:', userId);
  try {
    const endpoint = role === "admin" 
      ? `${API_URL}/admin/org-users/${userId}`
      : role === "subadmin"
        ? `${API_URL}/sub-admin/org-users/${userId}`
        : `${API_URL}/superadmin/org-users/${userId}`;
    const res = await axios.delete(endpoint, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    console.log('Delete response:', res.data);
    return { success: true, data: res.data };
  } catch (err) {
    console.error('Delete failed:', err.response?.data || err.message);
    return { success: false, error: err.response?.data?.message || 'Delete failed' };
  }
};

export const updateOrgUser = async ( userId, payload, role = "superadmin") => {
  try {
    const endpoint = role === "admin" 
      ? `${API_URL}/admin/org-users/${userId}`
      : role === "subadmin"
        ? `${API_URL}/sub-admin/org-users/${userId}`
        : `${API_URL}/superadmin/org-users/${userId}`;
    const res = await axios.put(endpoint, payload, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    console.log('Update response:', res.data);
    return { success: true, data: res.data };
  } catch (err) {
    console.error('Update failed:', err.response?.data || err.message);
    return { success: false, error: err.response?.data?.message || 'Update failed' };
  }
};

export const toggleUserStatus = async (userId, currentStatus, role = "superadmin") => {
  try {
    const isActive = !currentStatus;
    const endpoint = role === "admin"
      ? `${API_URL}/admin/org-users/status/${userId}`
      : role === "subadmin"
        ? `${API_URL}/sub-admin/org-users/status/${userId}`
        : `${API_URL}/superadmin/org-users/status/${userId}`;

    const res = await axios.patch(
      endpoint,
      { isActive },
      { headers: { Authorization: `Bearer ${getToken()}` } }
    );
    return { success: true, data: res.data };
  } catch (err) {
    return { success: false, error: err.response?.data?.message || 'Status update failed' };
  }
};

// --------------------- Profile APIs (GET /profile, PUT /profile, POST /profile/change-password) ---------------------
export const getProfile = async () => {
  try {
    const url = `${API_URL}/profile`;
    const res = await axios.get(url, { headers: { Authorization: `Bearer ${getToken()}` } });
    const norm = normalize(res);
    return { success: norm.success, data: norm.data };
  } catch (err) {
    return { 
      success: false, 
      status: err.response?.status || null, 
      error: err.response?.data?.message || err.response?.data?.error || 'Failed to fetch profile' 
    };
  }
};

export const updateProfile = async (payload) => {
  try {
    const url = `${API_URL}/profile`;
    const res = await axios.put(url, payload, { headers: { Authorization: `Bearer ${getToken()}` } });
    const norm = normalize(res);
    // update local userDetails if returned
    if (norm.success && norm.data) {
      try {
        const ud = JSON.parse(sessionStorage.getItem('userDetails') || '{}');
        const newUd = { ...ud, ...norm.data };
        sessionStorage.setItem('userDetails', JSON.stringify(newUd));
      } catch (e) {
        // ignore
      }
    }
    return { success: norm.success, data: norm.data };
  } catch (err) {
    return { success: false, error: err.response?.data?.message || 'Failed to update profile' };
  }
};

export const changePassword = async ({ oldPassword, newPassword }, forUser = false, userId = null) => {
  try {
    let url;
    if (forUser && userId) {
      url = `${API_URL}/users/${userId}/change-password`;
    } else {
      url = `${API_URL}/profile/change-password`;
    }

    const res = await axios.post(url, { oldPassword, newPassword }, { headers: { Authorization: `Bearer ${getToken()}` } });
    const norm = normalize(res);
    return { success: norm.success, data: norm.data };
  } catch (err) {
    return { success: false, error: err.response?.data?.message || 'Failed to change password' };
  }
};

/************************************Credit Cost setting API   **************************** */

// API Functions
export const fetchCreditCosts = async () => {
  try {
    const res = await axios.get(`${API_URL}/superadmin/credit-costs`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    console.log('Fetched credit costs:', res.data);
    return { success: true, data: res.data };
  } catch (err) {
    return { success: false, error: err.response?.data?.message || "Failed to load settings" };
  }
};

export const updateCreditCosts = async (payload) => {
  try {
    const res = await axios.put(`${API_URL}/superadmin/credit-costs`, payload, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return { success: true, data: res.data };
  } catch (err) {
    return { success: false, error: err.response?.data?.message || "Failed to save settings" };
  }
};

/************************************ADMIN PANEL APIs **************************** */

// Admin Organizations
export const getAdminOrganizations = async (search = "", page = 1, limit = 10, status = "all") => {
  try {
    const res = await axios.get(`${API_URL}/admin/organizations`, {
      params: { search, page, limit, status },
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    return normalize(res);
  } catch (err) {
    return { success: false, error: err.response?.data?.message || "Failed to load organizations" };
  }
};

export const getSubAdminOrganizations = async (search = "", page = 1, limit = 10, status = "all") => {
  try {
    const res = await axios.get(`${API_URL}/sub-admin/organizations`, {
      params: { search, page, limit, status },
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    return normalize(res);
  } catch (err) {
    return { success: false, error: err.response?.data?.message || "Failed to load organizations" };
  }
};

export const getAdminOrganizationById = async (id) => {
  try {
    const res = await axios.get(`${API_URL}/admin/organizations/${id}`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    return normalize(res);
  } catch (err) {
    return { success: false, error: err.response?.data?.message || "Failed to load organization" };
  }
};

export const updateAdminOrganization = async (id, payload) => {
  try {
    const res = await axios.put(`${API_URL}/admin/organizations/${id}`, payload, {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    return { success: true, data: res.data };
  } catch (err) {
    return { success: false, error: err.response?.data?.message || "Failed to update organization" };
  }
};

export const toggleAdminOrganizationStatus = async (id) => {
  try {
    const res = await axios.patch(`${API_URL}/admin/organizations/${id}/status`, {}, {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    return { success: true, data: res.data };
  } catch (err) {
    return { success: false, error: err.response?.data?.message || "Failed to update status" };
  }
};

// Admin Sub-Admins
export const getAdminSubAdmins = async (search = "", page = 1, limit = 10, status = "all") => {
  try {
    const res = await axios.get(`${API_URL}/admin/sub-admins`, {
      params: { search, page, limit, status },
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    return normalize(res);
  } catch (err) {
    return { success: false, error: err.response?.data?.message || "Failed to load sub-admins" };
  }
};

export const createAdminSubAdmin = async (payload) => {
  try {
    const res = await axios.post(`${API_URL}/admin/sub-admins`, payload, {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    return { success: true, data: res.data };
  } catch (err) {
    return { success: false, error: err.response?.data?.message || "Failed to create sub-admin" };
  }
};

export const updateAdminSubAdmin = async (id, payload) => {
  try {
    const res = await axios.put(`${API_URL}/admin/sub-admins/${id}`, payload, {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    return { success: true, data: res.data };
  } catch (err) {
    return { success: false, error: err.response?.data?.message || "Failed to update sub-admin" };
  }
};

export const deleteAdminSubAdmin = async (id) => {
  try {
    const res = await axios.delete(`${API_URL}/admin/sub-admins/${id}`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    return { success: true, data: res.data };
  } catch (err) {
    return { success: false, error: err.response?.data?.message || "Failed to delete sub-admin" };
  }
};

// Admin Users
export const getAdminOrgUsers = async (orgId) => {
  try {
    const res = await axios.get(`${API_URL}/admin/organizations/${orgId}/users`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    return normalize(res);
  } catch (err) {
    return { success: false, error: err.response?.data?.message || "Failed to load users" };
  }
};

export const createAdminOrgUser = async (payload) => {
  try {
    const res = await axios.post(`${API_URL}/admin/org-users`, payload, {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    return { success: true, data: res.data };
  } catch (err) {
    return { success: false, error: err.response?.data?.message || "Failed to create user" };
  }
};

// Admin / SubAdmin Transactions
export const getAdminTransactions = async ({ page = 1, limit = 20, type = '', search = '', dateFrom = '', dateTo = '' } = {}) => {
  try {
    const res = await axios.get(`${API_URL}/admin/transactions`, {
      params: { page, limit, type, search, dateFrom, dateTo },
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    return normalize(res);
  } catch (err) {
    return { success: false, error: err.response?.data?.message || "Failed to load transactions" };
  }
};

// SuperAdmin Transactions (org wallet recharges)
export const getSuperadminTransactions = async ({ page = 1, limit = 20, type = '', search = '', dateFrom = '', dateTo = '' } = {}) => {
  try {
    const res = await axios.get(`${API_URL}/superadmin/org-transactions`, {
      params: { page, limit, type, search, dateFrom, dateTo },
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    return normalize(res);
  } catch (err) {
    return { success: false, error: err.response?.data?.message || "Failed to load transactions" };
  }
};

export const getSuperadminDeductions = async ({ dateFrom = '', dateTo = '' } = {}) => {
  try {
    const res = await axios.get(`${API_URL}/superadmin/org-transactions/deductions`, {
      params: { dateFrom, dateTo },
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    return { success: true, data: res.data?.data || [] };
  } catch (err) {
    return { success: false, error: err.response?.data?.message || "Failed to load deductions" };
  }
};

export const addSuperadminDeduction = async ({ purpose, amount, updatedBy, date, utrNumber = '', paymentMode = '', attachmentUrl = '' }) => {
  try {
    const res = await axios.post(
      `${API_URL}/superadmin/org-transactions/deductions`,
      { purpose, amount, updatedBy, date, utrNumber, paymentMode, attachmentUrl },
      { headers: { Authorization: `Bearer ${getToken()}` } }
    );
    return { success: true, data: res.data?.data || null };
  } catch (err) {
    return { success: false, error: err.response?.data?.message || "Failed to add deduction" };
  }
};

// Admin Topup Requests approval/rejection
export const approveAdminTopup = async (id) => {
  try {
    const res = await axios.patch(`${API_URL}/admin/topup-requests/${id}/approve`, {}, {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    return { success: true, data: res.data };
  } catch (err) {
    return { success: false, error: err.response?.data?.message || "Failed to approve topup" };
  }
};

export const rejectAdminTopup = async (id) => {
  try {
    const res = await axios.patch(`${API_URL}/admin/topup-requests/${id}/reject`, {}, {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    return { success: true, data: res.data };
  } catch (err) {
    return { success: false, error: err.response?.data?.message || "Failed to reject topup" };
  }
};

// Admin Tickets
export const getAdminTickets = async () => {
  try {
    const res = await axios.get(`${API_URL}/admin/tickets`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    return normalize(res);
  } catch (err) {
    return { success: false, error: err.response?.data?.message || "Failed to load tickets" };
  }
};

export const assignAdminTicket = async (id, payload) => {
  try {
    const res = await axios.patch(`${API_URL}/admin/tickets/${id}/assign`, payload, {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    return { success: true, data: res.data };
  } catch (err) {
    return { success: false, error: err.response?.data?.message || "Failed to assign ticket" };
  }
};

export const resolveAdminTicket = async (id) => {
  try {
    const res = await axios.patch(`${API_URL}/admin/tickets/${id}/resolve`, {}, {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    return { success: true, data: res.data };
  } catch (err) {
    return { success: false, error: err.response?.data?.message || "Failed to resolve ticket" };
  }
};

// Admin Credit Checks
export const getAdminCreditChecks = async (orgId) => {
  try {
    const res = await axios.get(`${API_URL}/admin/organizations/${orgId}/credit-checks`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    return normalize(res);
  } catch (err) {
    return { success: false, error: err.response?.data?.message || "Failed to load credit checks" };
  }
};

// Admin Uploaded Data
export const getAdminUploadedData = async (orgId) => {
  try {
    const res = await axios.get(`${API_URL}/admin/organizations/${orgId}/uploaded-data`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    return normalize(res);
  } catch (err) {
    return { success: false, error: err.response?.data?.message || "Failed to load uploaded data" };
  }
};

// Delete Admin Uploaded File
export const deleteAdminUploadedData = async (fileId) => {
  try {
    const res = await axios.delete(`${API_URL}/admin/uploaded-data/${fileId}`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    return normalize(res);
  } catch (err) {
    return { success: false, error: err.response?.data?.message || "Failed to delete file" };
  }
};

// Download Uploaded File
export const downloadUploadedFile = async (fileId) => {
  try {
    const res = await axios.get(`${API_URL}/admin/uploaded-data/${fileId}/download`, {
      headers: { Authorization: `Bearer ${getToken()}` },
      responseType: 'blob'
    });
    return normalize(res);
  } catch (err) {
    return { success: false, error: err.response?.data?.message || "Failed to download file" };
  }
};

// Delete Admin Org User
export const deleteAdminOrgUser = async (userId) => {
  try {
    const res = await axios.delete(`${API_URL}/admin/org-users/${userId}`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    return normalize(res);
  } catch (err) {
    return { success: false, error: err.response?.data?.message || "Failed to delete user" };
  }
};

// Resend User Email
export const resendUserEmail = async (userId) => {
  try {
    const res = await axios.post(`${API_URL}/admin/org-users/${userId}/resend-email`, {}, {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    return normalize(res);
  } catch (err) {
    return { success: false, error: err.response?.data?.message || "Failed to resend email" };
  }
};

// Admin Topup Requests with org ID
export const getAdminTopupRequests = async (orgId) => {
  try {
    const res = await axios.get(`${API_URL}/admin/organizations/${orgId}/topup-requests`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    return normalize(res);
  } catch (err) {
    return { success: false, error: err.response?.data?.message || "Failed to load topup requests" };
  }
};

// Wallet Balance with org ID
export const getAdminWalletBalance = async (orgId) => {
  try {
    const res = await axios.get(`${API_URL}/admin/organizations/${orgId}/wallet-balance`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    return normalize(res);
  } catch (err) {
    return { success: false, error: err.response?.data?.message || "Failed to load wallet balance" };
  }
};

// Admin Bank Acts
export const getAdminBankActs = async () => {
  try {
    const res = await axios.get(`${API_URL}/admin/bank-acts`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    return normalize(res);
  } catch (err) {
    return { success: false, error: err.response?.data?.message || "Failed to load bank acts" };
  }
};

export const createAdminBankAct = async (payload) => {
  try {
    const res = await axios.post(`${API_URL}/admin/bank-acts`, payload, {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    return { success: true, data: res.data };
  } catch (err) {
    return { success: false, error: err.response?.data?.message || "Failed to create bank act" };
  }
};

export const updateAdminBankAct = async (id, payload) => {
  try {
    const res = await axios.put(`${API_URL}/admin/bank-acts/${id}`, payload, {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    return { success: true, data: res.data };
  } catch (err) {
    return { success: false, error: err.response?.data?.message || "Failed to update bank act" };
  }
};

export const deleteAdminBankAct = async (id) => {
  try {
    const res = await axios.delete(`${API_URL}/admin/bank-acts/${id}`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    return { success: true, data: res.data };
  } catch (err) {
    return { success: false, error: err.response?.data?.message || "Failed to delete bank act" };
  }
};

// Update Admin Ticket (general update function)
export const updateAdminTicket = async (id, payload) => {
  try {
    const res = await axios.patch(`${API_URL}/admin/tickets/${id}`, payload, {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    return { success: true, data: res.data };
  } catch (err) {
    return { success: false, error: err.response?.data?.message || "Failed to update ticket" };
  }
};
